import { type HTMLAttributes, forwardRef } from 'react';

import { cn } from '../utils/cn';

/**
 * Investment status types based on the requirements
 */
export type InvestmentStatus =
  | 'new'
  | 'submitted'
  | 'approved'
  | 'deferred'
  | 'rejected'
  | 'planned'
  | 'in_progress'
  | 'complete'
  | 'cancelled';

export interface StatusBadgeProps extends Omit<HTMLAttributes<HTMLSpanElement>, 'children'> {
  /**
   * Investment status
   */
  status: InvestmentStatus;

  /**
   * Badge size
   * @default 'md'
   */
  size?: 'sm' | 'md' | 'lg';

  /**
   * Whether to show a dot indicator
   * @default true
   */
  dot?: boolean;
}

/**
 * StatusBadge component
 *
 * A specialized badge for displaying investment statuses with predefined colors.
 * Maps to the investment workflow states defined in the requirements.
 *
 * @example
 * ```tsx
 * <StatusBadge status="approved" />
 * <StatusBadge status="in_progress" size="lg" />
 * <StatusBadge status="rejected" dot={false} />
 * ```
 */
export const StatusBadge = forwardRef<HTMLSpanElement, StatusBadgeProps>(
  ({ status, size = 'md', dot = true, className, ...props }, ref) => {
    const baseStyles = [
      'inline-flex items-center gap-1.5 rounded-full font-medium',
      'transition-colors duration-200',
    ];

    const sizeStyles = {
      sm: 'px-2 py-0.5 text-xs',
      md: 'px-2.5 py-0.5 text-sm',
      lg: 'px-3 py-1 text-base',
    };

    const dotSizeStyles = {
      sm: 'h-1.5 w-1.5',
      md: 'h-2 w-2',
      lg: 'h-2.5 w-2.5',
    };

    // Status configurations with colors and labels
    const statusConfig: Record<
      InvestmentStatus,
      { label: string; bgColor: string; textColor: string; dotColor: string }
    > = {
      new: {
        label: 'New',
        bgColor: 'bg-gray-100 dark:bg-gray-800',
        textColor: 'text-gray-800 dark:text-gray-200',
        dotColor: 'bg-gray-400 dark:bg-gray-500',
      },
      submitted: {
        label: 'Submitted',
        bgColor: 'bg-blue-100 dark:bg-blue-950',
        textColor: 'text-blue-800 dark:text-blue-200',
        dotColor: 'bg-blue-400 dark:bg-blue-500',
      },
      approved: {
        label: 'Approved',
        bgColor: 'bg-green-100 dark:bg-green-950',
        textColor: 'text-green-800 dark:text-green-200',
        dotColor: 'bg-green-400 dark:bg-green-500',
      },
      deferred: {
        label: 'Deferred',
        bgColor: 'bg-yellow-100 dark:bg-yellow-950',
        textColor: 'text-yellow-800 dark:text-yellow-200',
        dotColor: 'bg-yellow-400 dark:bg-yellow-500',
      },
      rejected: {
        label: 'Rejected',
        bgColor: 'bg-red-100 dark:bg-red-950',
        textColor: 'text-red-800 dark:text-red-200',
        dotColor: 'bg-red-400 dark:bg-red-500',
      },
      planned: {
        label: 'Planned',
        bgColor: 'bg-purple-100 dark:bg-purple-950',
        textColor: 'text-purple-800 dark:text-purple-200',
        dotColor: 'bg-purple-400 dark:bg-purple-500',
      },
      in_progress: {
        label: 'In Progress',
        bgColor: 'bg-indigo-100 dark:bg-indigo-950',
        textColor: 'text-indigo-800 dark:text-indigo-200',
        dotColor: 'bg-indigo-400 dark:bg-indigo-500',
      },
      complete: {
        label: 'Complete',
        bgColor: 'bg-emerald-100 dark:bg-emerald-950',
        textColor: 'text-emerald-800 dark:text-emerald-200',
        dotColor: 'bg-emerald-400 dark:bg-emerald-500',
      },
      cancelled: {
        label: 'Cancelled',
        bgColor: 'bg-gray-100 dark:bg-gray-800',
        textColor: 'text-gray-600 dark:text-gray-400',
        dotColor: 'bg-gray-400 dark:bg-gray-500',
      },
    };

    const config = statusConfig[status];

    return (
      <span
        ref={ref}
        className={cn(baseStyles, sizeStyles[size], config.bgColor, config.textColor, className)}
        {...props}
      >
        {dot && (
          <span
            className={cn('rounded-full', dotSizeStyles[size], config.dotColor)}
            aria-hidden="true"
          />
        )}
        {config.label}
      </span>
    );
  }
);

StatusBadge.displayName = 'StatusBadge';

/**
 * Helper function to get all available statuses
 */
export const getAvailableStatuses = (): InvestmentStatus[] => [
  'new',
  'submitted',
  'approved',
  'deferred',
  'rejected',
  'planned',
  'in_progress',
  'complete',
  'cancelled',
];

/**
 * Helper function to get status label
 */
export const getStatusLabel = (status: InvestmentStatus): string => {
  const labels: Record<InvestmentStatus, string> = {
    new: 'New',
    submitted: 'Submitted',
    approved: 'Approved',
    deferred: 'Deferred',
    rejected: 'Rejected',
    planned: 'Planned',
    in_progress: 'In Progress',
    complete: 'Complete',
    cancelled: 'Cancelled',
  };
  return labels[status];
};
